/**
 * Shared auth-token store.
 *
 * Primary auth uses django-allauth's headless SESSION-TOKEN strategy: an opaque,
 * server-side, revocable token sent in the `X-Session-Token` request header on
 * every API call. Firebase Hosting forwards headers but strips every cookie
 * except `__session`, which is why the token travels in a header, not a cookie.
 *
 * The token is persisted in `localStorage` under a single per-origin key. Each
 * app is its own subdomain and each environment its own host, so localStorage
 * isolates the token per app AND per env - something a shared cookie never
 * could. XSS exposure is mitigated by django-csp and short server-side session
 * lifetimes. A module-memory fallback keeps auth working for the tab when
 * localStorage is unavailable (e.g. Safari private mode).
 */

const STORAGE_KEY = 'raven:auth:v1'

type Listener = (token: string | null) => void

const listeners = new Set<Listener>()
let memoryToken: string | null = null

function notify(token: string | null): void {
  for (const listener of listeners) listener(token)
}

export function getAuthToken(): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored !== null) return stored
  } catch {
    // localStorage blocked; fall back to in-memory token.
  }
  return memoryToken
}

export function setAuthToken(token: string | null): void {
  memoryToken = token
  try {
    if (token) localStorage.setItem(STORAGE_KEY, token)
    else localStorage.removeItem(STORAGE_KEY)
  } catch {
    // localStorage blocked; the in-memory token holds for this tab.
  }
  notify(token)
}

export function clearAuthToken(): void {
  setAuthToken(null)
}

export function hasAuthToken(): boolean {
  return getAuthToken() !== null
}

/**
 * Subscribe to token changes (this tab via set/clear, and other tabs via the
 * `storage` event - so logging out in one tab propagates to the rest).
 * Returns an unsubscribe function.
 */
export function subscribeAuthToken(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) {
      memoryToken = event.newValue
      notify(event.newValue)
    }
  })
}
